import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import apiClient from "../../business/network/ApiClient";
import Tooltip from "../design/Shared/Tooltip";

const TOOLTIP_ID = "tooltipSuggested";

/**
 * One suggested-question card. The text is clamped to five lines with an ellipsis so a long
 * question cannot stretch its row; when the clamp actually hides something, hovering the card
 * shows the whole question in the house tooltip. A question that fits gets no tooltip.
 */
const SuggestedCard = ({ question, onClick }) => {
  const textRef = useRef(null);
  const [clamped, setClamped] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = textRef.current;
      if (!el) return;
      setClamped(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [question]);

  return (
    <button
      className="px-6 py-3 transition-colors bg-white hover:bg-violet/10 border border-violet rounded-[20px] w-full text-dimgray-200 text-start min-h-[120px]"
      style={{
        fontSize: "14px",
        lineHeight: "20px",
        fontWeight: 400,
        boxShadow:
          "1px -1px 4px 0px rgba(11, 119, 188, 0.05), -1px 1px 4px 0px rgba(11, 119, 188, 0.05)",
      }}
      onClick={onClick}
      data-tooltip-id={clamped ? TOOLTIP_ID : undefined}
      data-tooltip-content={clamped ? question : undefined}
    >
      <span ref={textRef} className="line-clamp-5">
        {question}
      </span>
    </button>
  );
};

const Suggested = ({ onSuggestedClick, userId }) => {
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);

  const handleSuggestedQuestion = (q) => {
    if (q) {
      onSuggestedClick(q);
    }
  };

  const getSuggestedQuestions = async () => {
    try {
      // userId lets the backend personalize (onboarding preferences, then the user's
      // own most-asked questions); without it we get the curated list.
      let questions = await apiClient.getSuggestedQuestions(userId);
      setSuggestedQuestions(Array.isArray(questions) ? questions : []);
    } catch (error) {
      setSuggestedQuestions([]);
    }
  };

  useEffect(() => {
    getSuggestedQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <>
      {/* Questions are user- or admin-written text: render the tooltip as text, never as HTML. */}
      <Tooltip
        id={TOOLTIP_ID}
        opacity={1}
        delayShow={300}
        render={({ content }) => <span>{content}</span>}
      />
      <div className="font-notosans gap-2 xl:gap-5 mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full">
        {suggestedQuestions &&
          suggestedQuestions.map((row, index) => {
            return (
              index < 3 && (
                <SuggestedCard
                  key={"q_" + index + "_" + row.id}
                  question={row?.question}
                  onClick={() => handleSuggestedQuestion(row?.question)}
                />
              )
            );
          })}
      </div>
    </>
  );
};

export default Suggested;
